import { useState } from "react";
import { ChevronDown, Database, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import {
  useGetUserSettingsQuery,
  useUpdateUserSettingsMutation,
} from "@/features/user-settings/infrastructure/api/userSettingsApi";

const ConfirmEnableDialog = ({
  open,
  onOpenChange,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading: boolean;
}) => {
  const { t } = useTranslation("settings");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("dataEncryption.confirmTitle")}
          </DialogTitle>
          <p className="mt-1 text-sm text-gray-500">
            {t("dataEncryption.confirmBody")}
          </p>
        </DialogHeader>

        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {t("dataEncryption.confirmWarning")}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("dataEncryption.cancel")}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            loading={loading}
            className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer"
          >
            {t("dataEncryption.confirmAction")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DataEncryptionControl = () => {
  const { t } = useTranslation("settings");
  const { data: settings, isLoading, isFetching } = useGetUserSettingsQuery();
  const [updateSettings, { isLoading: saving }] =
    useUpdateUserSettingsMutation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const encryptionEnabled = settings?.e2eEncryptionEnabled;
  const busy = isFetching || saving;

  if (isLoading || typeof encryptionEnabled !== "boolean") {
    return null;
  }

  // The switch controls data *sharing*: it is on when encryption is off.
  const sharingEnabled = !encryptionEnabled;

  const applySharingSetting = async (nextSharing: boolean) => {
    try {
      await updateSettings({ e2e_encryption_enabled: !nextSharing }).unwrap();
      toast.success(
        nextSharing
          ? t("dataEncryption.enabledToast")
          : t("dataEncryption.disabledToast"),
      );
    } catch {
      toast.error(t("dataEncryption.updateError"));
    }
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      setConfirmOpen(true);
      return;
    }
    void applySharingSetting(false);
  };

  const handleConfirmEnable = async () => {
    await applySharingSetting(true);
    setConfirmOpen(false);
  };

  return (
    <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center text-gray-500 [&_svg]:h-6 [&_svg]:w-6">
          <Database />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-gray-900">
            {t("dataEncryption.title")}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-gray-500">
            {t("dataEncryption.description")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:ml-auto">
          {busy && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
          <Switch
            checked={sharingEnabled}
            disabled={busy}
            onCheckedChange={handleToggle}
          />
        </div>
      </div>

      <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-1 text-sm font-medium text-primary"
          >
            {detailsOpen
              ? t("dataEncryption.hideDetails")
              : t("dataEncryption.viewDetails")}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                detailsOpen && "rotate-180",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-3 border-t border-gray-100 pt-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {t("dataEncryption.whatsSharedTitle")}
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-500">
              {t("dataEncryption.whatsSharedBody")}
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              {t("dataEncryption.howItsUsedTitle")}
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-gray-500">
              {t("dataEncryption.howItsUsedBody")}
            </p>
          </div>
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm leading-relaxed text-green-700">
            {t("dataEncryption.disclaimer")}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <ConfirmEnableDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => void handleConfirmEnable()}
        loading={saving}
      />
    </div>
  );
};
